import { useEffect, useMemo, useRef, useState } from "react";
import { IoIosCloseCircle } from "react-icons/io";

import styles from "./ImageUploader.module.css";

interface Props {
    image: File | string | null;
    label?: string;
    width?: number;
    height?: number;
    onImageSelected?: (file: File | null) => void;
}

export default function ImageUploader({
    image,
    label,
    width,
    height,
    onImageSelected,
}: Props) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [preview, setPreview] = useState<string | undefined>();

    const handleFiles = async (file: File) => {
        if (onImageSelected) onImageSelected(file);
    };

    const handleDrop = (ev: any) => {
        ev.preventDefault();

        if (ev.dataTransfer.files) {
            handleFiles(ev.dataTransfer.files[0]);
        }
    };

    useEffect(() => {
        if (!image) return setPreview(undefined);

        if (typeof image === "string")
            return setPreview(`${import.meta.env.VITE_SERVER_URL}${image}`);

        const objectUrl = URL.createObjectURL(image);
        setPreview(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [image]);

    return (
        <div
            className={styles.imageUploader}
            style={{
                ...(width ? { width: `${width}px` } : {}),
                ...(height ? { height: `${height}px` } : {}),
                ...(width || height ? { flexShrink: 0 } : {}),
            }}
            onDrop={handleDrop}
            onDragOver={(ev) => {
                ev.preventDefault();
            }}
            onClick={() => {
                fileInputRef.current?.click();
            }}
        >
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept="image/*"
                onChange={(e) => {
                    if (e.target.files?.[0]) handleFiles(e.target.files[0]);
                }}
            />
            {image && (
                <>
                    <img className={styles.preview} src={preview} />

                    <div className={styles.actionBar}>
                        <div
                            className={styles.actionButton}
                            onClick={(ev) => {
                                ev.stopPropagation();
                                if (onImageSelected) onImageSelected(null);
                            }}
                        >
                            <IoIosCloseCircle />
                        </div>
                    </div>
                </>
            )}
            {!image && <div>{label ? label : "Image"}</div>}
            {!image && width && height && (
                <div>
                    {width}x{height}px
                </div>
            )}
        </div>
    );
}
